/**
 * Storefront (:3002) adapter for the platform-wide client error capture.
 *
 * The generic logger in `./clientErrorLogger` is the same module the four Vite
 * apps carry; everything storefront-specific is here.
 *
 * ADDITION — nothing in the recovered bundle corresponds to this file.
 *
 * BROWSER ONLY. `installClientErrorLogger` returns immediately when there is no
 * `window`, so importing this from a module that also runs during SSR is safe,
 * but it is mounted from a client component (`ClientErrorCapture`) anyway so the
 * intent is explicit.
 */
import { installClientErrorLogger } from "./clientErrorLogger";
import { apiBaseUrl, activeTenantId } from "./config";
import { store } from "@/store";
import { staffUid } from "@/store/slices/authSlice";

/**
 * WHAT THIS SENDS ABOUT A PERSON, and what it does not.
 *
 * This is the one app whose "user" is a member of the public, so the rule is
 * stricter than elsewhere:
 *
 * - **NO phone number.** On this platform a customer's phone IS their identity
 *   and their login credential. It is never sent. The generic logger also
 *   redacts any run of 9+ digits from messages, stacks and URLs, so one cannot
 *   arrive by accident inside an error string either.
 * - **NO customer name, address, email or order.** A triage row does not need
 *   them, and a log that carries them is a customer database with no access
 *   control story.
 * - What IS sent for a customer is their opaque `_id` (already meaningless
 *   outside the database) or the literal `"guest"`. That is enough to answer
 *   "was this one shopper or two hundred" without identifying anyone.
 * - STAFF using the storefront's staff mode are treated like the other apps:
 *   their uid, name and role, because that is an employee acting in their job.
 */
function getContext() {
  // Runs inside an error handler, so the store may not be rehydrated yet.
  try {
    const s = store.getState();
    const auth = s.auth;
    if (auth?.isStaff && auth.staff) {
      return {
        tenant_id: s.settings?.data?._id || activeTenantId() || null,
        user_id: staffUid(auth.staff) || null,
        user_name: auth.staff.name || null,
        user_role: auth.staff.role || "staff",
      };
    }
    return {
      tenant_id: s.settings?.data?._id || activeTenantId() || null,
      user_id: auth?.customer?._id ? String(auth.customer._id) : "guest",
      user_name: null,
      user_role: "customer",
    };
  } catch {
    return null;
  }
}

export function initClientErrorCapture(): void {
  const base = apiBaseUrl();
  // With no API configured there is nowhere to send anything, and a relative
  // POST to "/client-errors" would hit this Next app's own 404 handler on every
  // batch. Do nothing instead.
  if (!base) return;
  installClientErrorLogger({
    platform: "WEB",
    // `apiBaseUrl()` is normally the same-origin proxy path `/api/backend`, so
    // the ingest call inherits the page's certificate — the same reason every
    // other storefront API call goes through it.
    endpoint: `${base.replace(/\/+$/, "")}/client-errors`,
    getContext,
    appVersion: process.env.NEXT_PUBLIC_APP_VERSION || null,
    enabled: process.env.NEXT_PUBLIC_CLIENT_ERROR_CAPTURE !== "off",
  });
}
